import { jsx } from 'react/jsx-runtime';
import { forwardRef, useState, useEffect } from 'react';
import { styled } from 'styled-components';
import { parseMarkdown, parseMarkdownAsync } from '../markdown.utils.js';
import { Flex } from '../../components/Flex.js';
import { Spinner } from '../../components/Spinner.js';
import { baseHtmlBasedPreviewStyles } from '../../html/html.config.js';
import { useClientLayoutEffect } from '@fabric-app/hooks/useClientLayoutEffect';
import { captureException } from '@fabric-app/compat/capabilities/sentry';
import { ensureError } from '@fabric-app/utils/guards';
import { useReferencedFn } from '@fabric-app/hooks/useReferencedFn';

const MarkdownDiv = styled.div.withConfig({
  displayName: "MarkdownRender__MarkdownDiv",
  componentId: "sc-4abc3bbf-0"
})([
  ``,
  ` `,
  ``
], baseHtmlBasedPreviewStyles, ({ fitToContainer }) => fitToContainer && "width: 100%;");
const MarkdownRender = /* @__PURE__ */ forwardRef(({ markdown, parserOptions, runAsync, onRender, ...props }, ref) => {
  const [content, setContent] = useState("");
  const [isLoaded, setIsLoaded] = useState(!runAsync);
  const onRenderRef = useReferencedFn(onRender);
  useClientLayoutEffect(() => {
    if (runAsync) return;
    setContent(parseMarkdown(markdown, parserOptions));
    setIsLoaded(true);
    onRenderRef.current?.();
  }, [
    markdown,
    parserOptions,
    runAsync
  ]);
  useEffect(() => {
    if (!runAsync) return;
    const asyncParse = async () => {
      setContent(await parseMarkdownAsync(markdown, parserOptions));
      setIsLoaded(true);
      onRenderRef.current?.();
    };
    asyncParse().catch((e) => {
      captureException(ensureError(e));
    });
  }, [
    markdown,
    parserOptions,
    runAsync,
    onRenderRef
  ]);
  if (!isLoaded) {
    return /* @__PURE__ */ jsx(Flex, {
      alignItems: "center",
      justifyContent: "center",
      flexGrow: true,
      minWidth: "100%",
      minHeight: "100%",
      children: /* @__PURE__ */ jsx(Spinner, {})
    });
  }
  return /* @__PURE__ */ jsx(MarkdownDiv, {
    ...props,
    ref,
    /**
     * @TODO - We should change how we parse markdown because this is a resource intensive operation.
     * Which causes the UI to freeze completely for 100ms to sometimes 800ms (depends on size).
     *
     * We should consider the following possibilites:
     * - Parse markdown to HTML serverse side which can be cached and sent to the client, this allows the client to avoid parsing markdown enitrely.
     * - Parse markdown in a web worker to avoid blocking the main thread, this should aleviate UI jank that ocurrs when parsing markdown.
     * - Similar to web worker, but with a service worker.
     * - Cache the parsed markdown into e.g. indexedDB, this allows the client to avoid parsing the same markdown several times.
     */
    dangerouslySetInnerHTML: {
      __html: content
    }
  });
});
MarkdownRender.displayName = "MarkdownRender";

export { MarkdownRender };
//# sourceMappingURL=MarkdownRender.js.map
